import axios from "axios";

/**
 * Resolves the API base URL at runtime using window.location.
 *
 * DEV  (localhost / 127.0.0.1):
 *   Frontend → http://localhost:8085/api/...
 *   Backend root-path = /api  (application.properties)
 *   Result: backend receives /api/users  ✅
 *
 * PROD (any other host, e.g. intranet.sinop.mt.gov.br):
 *   Frontend → /api/...  (same-origin, relative URL)
 *   Apache proxies  /api  →  http://backend:8085
 *   Backend root-path = /  (application-docker.properties / application-prod.properties)
 *   Result: Apache strips /api, backend receives /users  ✅
 *   NO /api/api double-prefix.
 *
 * No .env files. No build-time variables.
 */
const getBaseUrl = (): string => {
  return '/api';
};

/**
 * Resolves the frontend basename at runtime from the actual URL path.
 *
 * - Served at http://IP:3005/           → basename = ''
 * - Served at https://host/emendas/     → basename = '/emendas'
 *
 * Detects by checking if the current pathname starts with /emendas.
 */
export const getBasename = (): string => {
  const { pathname } = window.location;
  return pathname.startsWith('/emendas') ? '/emendas' : '';
};

const API_URL = getBaseUrl();
export const BASENAME = getBasename();

// Discard the former shared session; never import another tab's account.
for (const key of ['token', 'refreshToken', 'user']) {
  localStorage.removeItem(key);
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Debug: log each outgoing request so we can confirm the resolved URL
api.interceptors.request.use((config) => {
  try {
    const base = config.baseURL ?? "";
    const url = config.url ?? "";
    const resolvedUrl = `${base}${base.endsWith("/") || url.startsWith("/") ? "" : "/"}${url}`;
    console.debug("[api] Request:", config.method, resolvedUrl);
  } catch {
    console.debug("[api] Request (failed to format):", config.method, config.baseURL, config.url);
  }

  const token = sessionStorage.getItem("token");
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Logout callback — injected by AuthContext after mount to keep circular
// dependency out of this module. Falls back to clearing sessionStorage directly.
// ---------------------------------------------------------------------------
let _logoutHandler: (() => void) | null = null;

export const setLogoutHandler = (fn: () => void): void => {
  _logoutHandler = fn;
};

const doLogout = (): void => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
  if (_logoutHandler) _logoutHandler();
  if (!window.location.pathname.endsWith('/login')) {
    window.location.href = `${BASENAME}/login?expired=true`;
  }
};

// ---------------------------------------------------------------------------
// Response interceptor — silent refresh with request queue (AC2, AC3, AC5)
// ---------------------------------------------------------------------------
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

const processQueue = (error: unknown, token: string | null): void => {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // If another refresh is already in flight, queue this request
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!refreshToken) {
      isRefreshing = false;
      doLogout();
      return Promise.reject(error);
    }

    try {
      // Use plain axios — not the intercepted instance — to avoid an infinite loop
      const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
        `${API_URL}/auth/refresh`,
        { refreshToken }
      );
      const newAccessToken = data.accessToken;
      sessionStorage.setItem('token', newAccessToken);
      sessionStorage.setItem('refreshToken', data.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
      processQueue(null, newAccessToken);
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      doLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Extrai uma mensagem de erro amigável de uma resposta da API.
 *
 * O backend já manda mensagem específica pra bloqueio de escopo (ex.:
 * validarSecretariaPodeAgir) — por isso essa função NUNCA sobrescreve
 * `response.data.error` quando ele existe, só melhora o fallback pros casos
 * em que a API não mandou nada: um 403 sem corpo passa a dizer "seu perfil
 * não tem acesso", em vez do mesmo texto genérico usado pra qualquer erro
 * (incluindo lista vazia por falta de dado, que é uma causa diferente).
 */
export const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === 'string' && response.data.error.trim()) {
      return response.data.error;
    }
    if (response?.status === 403) {
      return 'Seu perfil não tem acesso a este recurso.';
    }
  }
  return fallback;
};

export default api;
